<template>
  <q-card
    bordered
    flat
    class="card"
    :class="[
      borderColorClass,
      backgroundColor,
      noPadding ? 'no-padding' : ''
    ]"
  >
    <q-card-section
      v-if="$slots.header || headerIcon || header"
      class="header"
      :class="[color ? `border-${color} bg-${color}` : '']"
    >
      <slot name="header">
        <span class="text-poppins font-medium line-clamp-1">{{ header }}</span>
      </slot>
      <q-space />
      <q-btn
        v-if="headerIcon"
        :icon="headerIcon"
        flat
        size="sm"
        class="!rounded-xl header-icon"
        @click="emit('header-icon-click')"
      />
    </q-card-section>

    <q-card-section
      :class="{
        'overflow-y-auto': maxHeight,
      }"
      :style="{ maxHeight }"
    >
      <slot>
        Card content goes here
      </slot>

      <q-inner-loading :showing="loading">
        <q-spinner-ios size="50px" />
      </q-inner-loading>
    </q-card-section>
  </q-card>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  header: {
    type: String,
    default: '',
  },
  headerIcon: {
    type: String,
    default: '',
  },
  maxHeight: {
    type: String,
    default: null,
  },
  backgroundColor: {
    type: String,
    required: false,
    validator: (value) =>
      value.startsWith('#') ||
      [
        'bg-primary',
        'bg-secondary',
        'bg-dark',
        'bg-darkLight',
        'bg-accent',
        'bg-warning',
        'bg-negative',
        'bg-positive',
      ].includes(value),
    default: 'bg-primary',
  },
  borderColor: {
    type: String,
    required: false,
    default: '',
  },
  color: {
    type: [String],
    required: false,
    default: '',
    validator: (value) =>
      ['', 'primary', 'secondary', 'dark', 'accent', 'warning', 'negative', 'positive'].includes(
        value,
      ),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  noPadding: {
    type: Boolean,
    default: false,
  },
})

const borderColorClass = computed(() => {
  if (props.color) {
    return `border-${props.color}`;
  }
  if (!props.borderColor) {
    return 'border-secondary';
  }

  // borderColor will be binded to the card
  return '';
})

const emit = defineEmits(['header-icon-click'])
</script>

<style scoped>
.card {
  @apply border-2 border-secondary;
  @apply !rounded-xl;

  background-color: v-bind(backgroundColor);
  border-color: v-bind(borderColor);

  & > .q-card__section {
    @apply px-4 py-3;
  }

  &.no-padding > .q-card__section {
    @apply p-0;
  }

  .header {
    @apply flex;
    @apply bg-darkLight;
    @apply border-b-2 h-20 flex items-center border-secondary;
    @apply !rounded-[10px] !rounded-b-[10px];
    @apply pr-2 text-lg font-bold;
  }
}
</style>
